import { DestroyRef, effect, inject, InjectionToken, Injector, isDevMode, Signal, untracked } from '@angular/core';

import { injectSchemaResolver } from '../address/schema-resolver';
import { DataState } from '../model/data-state';
import { evaluateSchemaCondition } from '../model/schema-condition';
import { SCHEMA_FEATURE_FLAGS } from '../model/schema-feature-flags';
import { SchemaModalDeclaration, SchemaModalsConfig } from '../model/schema-node.types';

/**
 * Whoever opens the modals schema nodes declare. Core hands each declaration over and opens nothing itself, which
 * keeps `@angular/router` out of this library: the URL-modals engine in `@kros-sk/shared/modal-sheet` implements it,
 * and `provideUrlModals()` provides it.
 */
export interface SchemaModalHost {
  /**
   * Declares `declaration` under `modalId` until the returned function runs. `injector` is the declaring node's, so the
   * modal renders under it and reads that node's actions, source and query. The declaration comes without `showIf`:
   * the node decided it before handing the entry over.
   */
  declare(modalId: string, declaration: SchemaModalDeclaration, injector: Injector): () => void;
}

export const SCHEMA_MODAL_HOST = new InjectionToken<SchemaModalHost>('SCHEMA_MODAL_HOST');

/**
 * Hands a node's `modals` to the modal host for as long as the calling node lives. Runs in the node's scope, so the
 * injector it hands over is the node's.
 *
 * An entry's own `showIf` decides whether it is declared, the way a child's decides whether the child exists: on
 * `state`, the state the node hands down — its own source's when it has one — and on the node's source as `$source`.
 * Each entry is declared while its condition holds and taken back when it stops, or when the node's `modals` replace
 * it, and the others stay as they are: taking an entry back cancels its modal if the URL holds it.
 *
 * Without a host, a node declaring any warns in dev mode and declares nothing.
 */
export function declareSchemaModals(modals: Signal<SchemaModalsConfig | undefined>, state: Signal<DataState | undefined>): void {
  const host = inject(SCHEMA_MODAL_HOST, { optional: true });

  if (!host) {
    effect(() => {
      const modalIds = Object.keys(modals() ?? {});

      if (modalIds.length > 0) {
        reportMissingHost(modalIds);
      }
    });

    return;
  }

  const injector = inject(Injector);
  const schema = injectSchemaResolver(state);
  const featureFlags = inject(SCHEMA_FEATURE_FLAGS, { optional: true });
  const declared = new Map<string, { entry: SchemaModalDeclaration; undeclare: () => void }>();

  effect(() => {
    const wanted = new Map(
      Object.entries(modals() ?? {}).filter(([, entry]) => evaluateSchemaCondition(entry.showIf, state(), schema, featureFlags))
    );

    untracked(() => {
      declared.forEach(({ entry, undeclare }, modalId) => {
        if (wanted.get(modalId) !== entry) {
          undeclare();
          declared.delete(modalId);
        }
      });

      wanted.forEach((entry, modalId) => {
        if (!declared.has(modalId)) {
          declared.set(modalId, { entry, undeclare: host.declare(modalId, withoutCondition(entry), injector) });
        }
      });
    });
  });

  inject(DestroyRef).onDestroy(() => declared.forEach(({ undeclare }) => undeclare()));
}

/** The entry as the modal renders it. Decided again there, on the params, its condition could only empty the window. */
function withoutCondition({ showIf, ...declaration }: SchemaModalDeclaration): SchemaModalDeclaration {
  return declaration;
}

function reportMissingHost(modalIds: string[]): void {
  if (isDevMode()) {
    console.warn(
      `A node declares the modals ${modalIds.map((modalId) => `"${modalId}"`).join(', ')}, but nothing here opens schema modals, ` +
        'so none of them can open. Provide SCHEMA_MODAL_HOST above the node — provideUrlModals() does.'
    );
  }
}
